import { useMutation, useQuery } from '@tanstack/react-query';
import { FormApi, ValidationErrors } from 'final-form';
import arrayMutators from 'final-form-arrays';
import R from 'ramda';
import React, { useCallback, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import ErrorToastContent from 'common/components/ErrorToastContent';
import validateYupSchema from 'common/utils/validateYupSchema';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { MultiImageValue } from 'modules/menu/components/ImageUploadInput/MultiImageUploadInput';
import MenuEditorConfirmationPrompt from 'modules/menu/components/MenuEditorConfirmationPrompt';
import { useVatCategories } from 'modules/menu/components/VatCategories';
import { getModifiedFieldsForTracking, useSaveItemChangesTracking } from 'modules/menu/hooks/telemetryHooks';
import { CommissionConfirmationModal } from 'modules/menu/items/components/Item/ItemCommissions/CommissionConfirmationModal';
import { ItemQuantity } from 'modules/menu/items/types';
import {
  createOfferBinding,
  deleteOfferBinding,
  getProductInformationSettings,
  updateOffer,
  updateOfferOverrides,
  updateVenueInventory,
} from 'modules/shared-menu/api';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { UpdateOfferOverridesPayload } from 'modules/shared-menu/types';

import { OfferDetailsFormFields, FormValues, ValidationContext, validationSchema } from './OfferDetailsForm';
import { offerTabs } from './OfferDetailsForm/constants';
import {
  buildOverridesPayload,
  transformOfferApiToFormValues,
  transformOfferFormValuesToApi,
} from './OfferDetailsForm/transformers';
import { getTabsErrors } from './OfferDetailsForm/utils';
import { OfferStickyFooter } from './OfferStickyFooter';
import { cleanUpInvalidPrerequisiteOptionChoices } from './OptionBindings/utils';
import { useSelectionInfoContext } from '../../../contexts/SelectionInfoContext';
import { useSelectionData } from '../../../hooks/useSelectionData';
import { venueLevelOnlyAttrs } from '../constants';
import { useOfferOverridesContext } from '../context/OfferOverridesContext';
import { useOfferTabContext } from '../context/OfferTabContext';
import { useProcessSelectionImage } from '../hooks/useProcessSelectionImage';
import { useVenueSelectionOverrides } from '../hooks/useSelectionOverrides';
import { useVenueOfferCommissions } from '../hooks/useVenueOfferCommissions';
import { Offer } from '../types';

type EditOfferProps = {
  categoryId: string | null;
  offer: Offer;
  isEditing: boolean;
  setIsEditing: (state: boolean) => void;
  onClose: () => void;
  offerQuantity?: ItemQuantity;
  refetchOfferInventory: () => void;
};

type UpdateOfferMutationVariables = {
  selectionId: string;
  offerId: string;
  payload: Offer;
};

type CreateOfferBindingMutationVariables = {
  selectionId: string;
  categoryId: string;
  offerId: string;
};

type UpdateOfferOverridesMutationVariables = {
  selectionId: string;
  offerId: string;
  payload: UpdateOfferOverridesPayload;
};

type DeleteOfferBindingMutationVariables = CreateOfferBindingMutationVariables;

type UpdateOfferInventoryVariables = {
  offerId: string;
  total: number;
};

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledOfferDetailsFormFields = styled(OfferDetailsFormFields)`
  flex: 1 1 auto;
  overflow: auto;
  padding: 0 24px;
`;

const commissionsProperties: (keyof FormValues)[] = ['homedeliveryCommission', 'takeawayCommission', 'eatinCommission'];

const nonSpecificOfferProperties: (keyof FormValues)[] = ['categoryId', 'inventoryBalance', ...commissionsProperties];

const hasCategoryChanged = (previousValues: FormValues, currentValues: FormValues) =>
  previousValues.categoryId !== currentValues.categoryId;

const hasOfferChanged = (previousValues: FormValues, currentValues: FormValues) =>
  !R.equals(R.omit(nonSpecificOfferProperties, previousValues), R.omit(nonSpecificOfferProperties, currentValues));

const hasInventoryBalanceChanged = (previousValues: FormValues, currentValues: FormValues) =>
  previousValues.inventoryBalance !== currentValues.inventoryBalance;

const haveCommissionsChanged = (previousValues: FormValues, currentValues: FormValues) =>
  !R.equals(R.pick(commissionsProperties, previousValues), R.pick(commissionsProperties, currentValues));

export const EditOffer = ({
  categoryId,
  offer,
  onClose,
  isEditing,
  setIsEditing,
  offerQuantity,
  refetchOfferInventory,
}: EditOfferProps) => {
  const menuFeatures = useOfferingMenuFeaturesContext();
  const { currency, alcohol_warning_permille_limit: alcoholWarningPermilleLimit } = useCountryConfigContext();
  const { data: vatCategories } = useVatCategories();
  const trackSaveChanges = useSaveItemChangesTracking();
  const { offerOverrides, currOverriddenRowGroups, currOverriddenOfferAttrs } = useOfferOverridesContext();
  const { refetchSelectionOverrides } = useVenueSelectionOverrides();
  const { vlmeConfig } = useSelectionInfoContext();
  const { activeTabId, showTabErrors, hideTabErrors } = useOfferTabContext();
  const scope = useScopeContext();
  const { selection, categories, updateSelectionData, options } = useSelectionData();
  const [nutritionFactsFormat, setNutritionFactsFormat] = useState<'text' | 'table'>('table');
  const [isCommissionConfirmationModalOpen, setCommissionConfirmationModalOpen] = useState(false);
  const { getOfferCommissions, createCommission, updateCommission, deleteCommission } = useVenueOfferCommissions();

  const existingOfferCommissions = useMemo(() => getOfferCommissions(offer.id), [getOfferCommissions, offer.id]);

  const processSelectionImage = useProcessSelectionImage();

  const { data: productInfoSettings } = useQuery(
    ['product-information-settings', selection.id],
    getProductInformationSettings,
    { refetchOnMount: false, refetchOnWindowFocus: false },
  );

  const initialFormValues = useMemo(() => {
    const values = transformOfferApiToFormValues(offer, {
      alcoholWarningPermilleLimit,
      language: selection.primary_language,
      categoryId,
      vatCategories: vatCategories?.list,
      productInformationSettings: productInfoSettings,
      offerOverrides,
      itemBalance: offerQuantity?.balance,
      offerCommissions: existingOfferCommissions,
    });
    setNutritionFactsFormat(values.nutritionValues.some(n => n.value !== undefined) ? 'table' : 'text');
    return values;
  }, [
    offer,
    alcoholWarningPermilleLimit,
    selection.primary_language,
    categoryId,
    vatCategories?.list,
    productInfoSettings,
    offerOverrides,
    offerQuantity?.balance,
    existingOfferCommissions,
  ]);

  const validationContext = useMemo<ValidationContext>(
    () => ({ currency, currOverriddenRowGroups }),
    [currOverriddenRowGroups, currency],
  );

  const { mutateAsync: executeProcessSelectionImage } = useMutation((image: MultiImageValue) =>
    processSelectionImage(image),
  );

  const { mutateAsync: executeUpdateOffer } = useMutation(
    ({ selectionId, offerId, payload }: UpdateOfferMutationVariables) => updateOffer(selectionId, offerId, payload),
    {
      onSuccess: updatedOffer => {
        updateSelectionData({ actionType: 'updateOffer', payload: updatedOffer });
      },
    },
  );

  const { mutateAsync: executeCreateOfferBinding } = useMutation(
    ({ selectionId, categoryId, offerId }: CreateOfferBindingMutationVariables) =>
      createOfferBinding(selectionId, categoryId, offerId),
    {
      onSuccess: updatedSelection => {
        updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
      },
    },
  );

  const { mutateAsync: executeDeleteOfferBinding } = useMutation(
    ({ selectionId, categoryId, offerId }: DeleteOfferBindingMutationVariables) =>
      deleteOfferBinding(selectionId, categoryId, offerId),
    {
      onSuccess: updatedSelection => {
        updateSelectionData({ actionType: 'updateSelection', payload: updatedSelection });
      },
    },
  );

  const { mutateAsync: executeUpdateOfferOverrides } = useMutation(
    ({ selectionId, offerId, payload }: UpdateOfferOverridesMutationVariables) =>
      updateOfferOverrides(selectionId, offerId, payload),
    {
      onSuccess: () => {
        refetchSelectionOverrides();
        onClose();
      },
    },
  );

  const { mutateAsync: executeUpdateOfferInventory } = useMutation(
    ({ total, offerId }: UpdateOfferInventoryVariables) => updateVenueInventory(scope.id, total, offerId),
    {
      onSuccess: () => {
        refetchOfferInventory();
      },
    },
  );

  const handleCategoryUpdate = useCallback(
    async (selectionId: string, offerId: string, oldCategoryId: string | null, newCategoryId: string | null) => {
      if (newCategoryId) {
        await executeCreateOfferBinding({
          selectionId,
          categoryId: newCategoryId,
          offerId,
        });
        return;
      }

      if (!oldCategoryId) {
        return;
      }

      await executeDeleteOfferBinding({
        selectionId,
        categoryId: oldCategoryId,
        offerId,
      });
    },
    [executeCreateOfferBinding, executeDeleteOfferBinding],
  );

  const handleOfferSubmit = useCallback(
    async (formValues: FormValues) => {
      const validValues = validationSchema.validateSync(formValues, { context: validationContext });

      try {
        const imageUrls = await Promise.all(
          validValues.image.map(async image => {
            return await executeProcessSelectionImage(image);
          }),
        );
        const definedImageUrls = imageUrls.filter((url: string | null): url is string => url !== null);

        const haveOptionBindingsChanged = !R.equals(initialFormValues.optionBindings, validValues.optionBindings);

        const optionBindings = haveOptionBindingsChanged
          ? cleanUpInvalidPrerequisiteOptionChoices(
              validValues.optionBindings ?? [],
              initialFormValues.optionBindings,
              options,
            )
          : validValues.optionBindings;

        const validValuesWithCleanedUpOptionBindings = {
          ...validValues,
          optionBindings,
        };

        const payload = transformOfferFormValuesToApi(offer, validValuesWithCleanedUpOptionBindings, definedImageUrls, {
          language: selection.primary_language,
          currency,
          nutritionFactsFormat,
          productHierarchies: productInfoSettings?.product_hierarchy_levels ?? [],
          externalInfoHyperlinkAllowed: menuFeatures.externalInfoHyperlinkAllowed,
        });

        await executeUpdateOffer({ selectionId: selection.id, offerId: offer.id, payload });
      } catch (error) {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
        return;
      }
    },
    [
      validationContext,
      selection.id,
      selection.primary_language,
      offer,
      initialFormValues.optionBindings,
      options,
      currency,
      nutritionFactsFormat,
      productInfoSettings?.product_hierarchy_levels,
      menuFeatures.externalInfoHyperlinkAllowed,
      executeUpdateOffer,
      executeProcessSelectionImage,
    ],
  );

  const handleOverridesSubmit = useCallback(
    async (formValues: FormValues) => {
      const validFormValues = validationSchema.validateSync(formValues, {
        context: validationContext,
      });

      const overrides = buildOverridesPayload({
        values: validFormValues,
        currOverriddenOfferAttrs,
        venueLevelOnlyAttrs,
        currency,
      });

      try {
        await executeUpdateOfferOverrides({
          selectionId: selection.id,
          offerId: offer.id,
          payload: { scope, ...overrides },
        });
      } catch (error) {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      }
    },
    [validationContext, currOverriddenOfferAttrs, currency, executeUpdateOfferOverrides, selection.id, offer.id, scope],
  );

  const handleInventoryUpdate = useCallback(
    async (updatedInventoryBalance: number) => {
      try {
        await executeUpdateOfferInventory({ offerId: offer.id, total: updatedInventoryBalance });
      } catch (e) {
        toast.error(<ErrorToastContent error={e} />, { duration: Infinity });
      }
    },
    [executeUpdateOfferInventory, offer.id],
  );

  const handleCommissionsUpdate = useCallback(
    async (formValues: FormValues) => {
      if (!existingOfferCommissions) {
        await createCommission({
          offerId: offer.id,
          commissionFormValues: {
            homedeliveryCommission: formValues.homedeliveryCommission,
            eatinCommission: formValues.eatinCommission,
            takeawayCommission: formValues.takeawayCommission,
          },
        });
        return;
      }

      if (
        formValues.homedeliveryCommission === undefined &&
        formValues.eatinCommission === undefined &&
        formValues.takeawayCommission === undefined
      ) {
        await deleteCommission({ offerId: offer.id });
        return;
      }

      await updateCommission({
        offerId: offer.id,
        commissionFormValues: {
          homedeliveryCommission: formValues.homedeliveryCommission,
          eatinCommission: formValues.eatinCommission,
          takeawayCommission: formValues.takeawayCommission,
        },
      });
    },
    [createCommission, deleteCommission, updateCommission, offer.id, existingOfferCommissions],
  );

  const handleUpdateOffer = useCallback(
    async (formValues: FormValues, form: FormApi<FormValues>) => {
      if (hasCategoryChanged(initialFormValues, formValues)) {
        handleCategoryUpdate(selection.id, offer.id, initialFormValues.categoryId, formValues.categoryId);
      }

      if (hasOfferChanged(initialFormValues, formValues)) {
        if (vlmeConfig?.isSharedVenue) {
          await handleOverridesSubmit(formValues);
        } else {
          await handleOfferSubmit(formValues);
        }
      }

      if (hasInventoryBalanceChanged(initialFormValues, formValues) && formValues.inventoryBalance !== undefined) {
        await handleInventoryUpdate(formValues.inventoryBalance);
      }

      // Commission update operation has a dependency on the previous ones to be completed
      // (Backend will send an event to financial-data-registry relying on up-to-date item information)
      if (!!vlmeConfig && haveCommissionsChanged(initialFormValues, formValues)) {
        await handleCommissionsUpdate(formValues);
      }

      const modifiedFields = getModifiedFieldsForTracking(form.getState());
      trackSaveChanges(offer.id, !!formValues.image.length, formValues.mandatoryWarnings, modifiedFields);
      onClose();
    },
    [
      initialFormValues,
      vlmeConfig,
      trackSaveChanges,
      offer.id,
      onClose,
      handleCategoryUpdate,
      selection.id,
      handleOverridesSubmit,
      handleOfferSubmit,
      handleInventoryUpdate,
      handleCommissionsUpdate,
    ],
  );

  const highlightAllFieldsWithErrors = useCallback(
    (errors: ValidationErrors, form: FormApi<FormValues>) => {
      if (errors) {
        Object.keys(errors).forEach((fieldName: keyof FormValues) => form.blur(fieldName));
        showTabErrors();
      }
    },
    [showTabErrors],
  );

  return categories ? (
    <Form<FormValues>
      initialValues={initialFormValues}
      onSubmit={handleUpdateOffer}
      mutators={{ ...arrayMutators }}
      validate={validateYupSchema(validationSchema, validationContext)}
    >
      {({ handleSubmit, submitting, dirty, form, errors, values }) => (
        <FormContainer
          onSubmit={e => {
            e.preventDefault();
            const tabsErrors = getTabsErrors(errors);
            const hasErrors = Object.values(tabsErrors).some(errorCount => errorCount > 0);

            if (hasErrors) {
              highlightAllFieldsWithErrors(errors, form);
            } else if (haveCommissionsChanged(initialFormValues, values)) {
              setCommissionConfirmationModalOpen(true);
            } else {
              handleSubmit();
            }
          }}
        >
          <CommissionConfirmationModal
            contentName={initialFormValues.name}
            isOpen={isCommissionConfirmationModalOpen}
            onClose={() => setCommissionConfirmationModalOpen(false)}
            onConfirm={() => {
              setCommissionConfirmationModalOpen(false);
              handleSubmit();
            }}
          />

          <StyledOfferDetailsFormFields
            categoryId={categoryId}
            sidePanelMode={isEditing ? 'edit' : 'preview'}
            primaryLanguage={selection.primary_language}
            onChangeNutritionFactsFormat={setNutritionFactsFormat}
            nutritionFactsFormat={nutritionFactsFormat}
            productInfoSettings={productInfoSettings}
            offer={offer}
            key={offer.id}
            offerQuantity={offerQuantity}
          />
          {isEditing && activeTabId !== offerTabs.OPTIONS && (
            <OfferStickyFooter
              isLoading={submitting}
              onCancel={() => {
                form.reset();
                hideTabErrors();
                setIsEditing(false);
              }}
            />
          )}
          <MenuEditorConfirmationPrompt when={dirty && !submitting} />
        </FormContainer>
      )}
    </Form>
  ) : null;
};
